import { z } from 'zod';
import { ToolOptions, ResourceOptions } from './types.js';
import { MetadataRegistry, ToolMetadata, ResourceMetadata } from './metadata.js';
import { ContextFactory } from './context.js';

// Global registry instance - will be replaced with per-server instance
let globalRegistry: any = null;
let globalMetadataRegistry: MetadataRegistry | null = null;

/**
 * Set the registry instance for decorators to use
 */
export function setRegistry(registry: any, metadataRegistry?: MetadataRegistry): void {
  globalRegistry = registry;
  globalMetadataRegistry = metadataRegistry || new MetadataRegistry();
}

/**
 * Extract JSDoc description from function
 */
function extractDescription(fn: Function): string {
  const fnStr = fn.toString();
  const match = fnStr.match(/\/\*\*([\s\S]*?)\*\//);
  if (match) {
    const jsdoc = match[1];
    // Extract the first line that's not a @param or @returns
    const lines = jsdoc.split('\n').map(line => line.trim().replace(/^\*/, '').trim());
    for (const line of lines) {
      if (line && !line.startsWith('@')) {
        return line;
      }
    }
  }
  return 'No description provided';
}

/**
 * Extract parameter information from JSDoc
 */
function extractParams(fn: Function): Record<string, string> {
  const fnStr = fn.toString();
  const match = fnStr.match(/\/\*\*([\s\S]*?)\*\//);
  const params: Record<string, string> = {};
  
  if (match) {
    const jsdoc = match[1];
    const paramMatches = jsdoc.matchAll(/@param\s+(\w+)\s*-\s*(.+)/g);
    for (const match of paramMatches) {
      params[match[1]] = match[2].trim();
    }
  }
  
  return params;
}

/**
 * Simplified Tool decorator that infers most information automatically
 */
export function Tool(options: Partial<ToolOptions> = {}) {
  return function (target: any, propertyKey: string, descriptor: PropertyDescriptor) {
    const originalMethod = descriptor.value;
    
    // Extract information from the method
    const description = options.description || extractDescription(originalMethod);
    const name = options.name || propertyKey;
    
    // Use provided input schema or create a basic one
    let inputSchema = options.input;
    if (!inputSchema) {
      // For now, use a generic schema - in practice, you'd analyze the parameter types
      inputSchema = z.object({}).passthrough();
    }
    
    const toolOptions: ToolOptions = {
      name,
      input: inputSchema,
      description,
      ...options
    };
    
    descriptor.value = async function (...args: any[]) {
      if (!globalRegistry) {
        throw new Error('Registry not initialized. Make sure to call BeastMcpServer.initialize()');
      }

      // Create context using factory
      const context = ContextFactory.create(name, 'session-' + Date.now(), 'host-' + Date.now());

      // Validate input using zod schema
      try {
        const validatedArgs = toolOptions.input.parse(args[0]);
        args[0] = validatedArgs;
      } catch (error) {
        throw new Error(`Validation failed for tool '${name}': ${error}`);
      }

      // Call the original method with context
      return originalMethod.apply(this, [args[0], context]);
    };

    // Register with registry
    if (globalRegistry) {
      globalRegistry.registerTool(name, toolOptions, descriptor.value);
    }

    // Register metadata
    if (globalMetadataRegistry) {
      const metadata: ToolMetadata = {
        id: name,
        options: toolOptions,
        handler: descriptor.value,
        className: target.constructor.name,
        methodName: propertyKey
      };
      globalMetadataRegistry.registerTool(metadata);
    }

    return descriptor;
  };
}

/**
 * Simplified Resource decorator
 */
export function Resource(options: Partial<ResourceOptions> = {}) {
  return function (target: any, propertyKey: string, descriptor: PropertyDescriptor) {
    const originalMethod = descriptor.value;
    
    const description = options.description || extractDescription(originalMethod);
    const name = options.name || propertyKey;
    
    const resourceOptions: ResourceOptions = {
      name,
      description,
      ...options
    };
    
    descriptor.value = async function (...args: any[]) {
      if (!globalRegistry) {
        throw new Error('Registry not initialized. Make sure to call BeastMcpServer.initialize()');
      }

      // Create context using factory
      const context = ContextFactory.create(name, 'session-' + Date.now(), 'host-' + Date.now());

      // Call the original method with context
      return originalMethod.apply(this, [context]);
    };

    // Register with registry
    if (globalRegistry) {
      globalRegistry.registerResource(name, resourceOptions, descriptor.value);
    }

    // Register metadata
    if (globalMetadataRegistry) {
      const metadata: ResourceMetadata = {
        id: name,
        options: resourceOptions,
        handler: descriptor.value,
        className: target.constructor.name,
        methodName: propertyKey
      };
      globalMetadataRegistry.registerResource(metadata);
    }

    return descriptor;
  };
}

/**
 * Even simpler decorator that just takes a description
 */
export function SimpleTool(description: string) {
  return Tool({ description });
}

/**
 * Simple resource decorator
 */
export function SimpleResource(description: string) {
  return Resource({ description });
}

/**
 * Example usage of simplified decorators:
 * 
 * // Option 1: JSDoc-based (recommended)
 * /**
 *  * Summarizes input text using a simple algorithm
 *  * @param text - The text to be summarized
 *  * @param maxLength - Maximum length of summary (optional)
 *  *\/
 * @Tool()
 * async summarizeText(
 *   { text, maxLength = 100 }: { text: string; maxLength?: number },
 *   context: any
 * ) {
 *   // Implementation
 * }
 * 
 * // Option 2: Description-only
 * @SimpleTool('Summarizes input text using a simple algorithm')
 * async summarizeText(
 *   { text, maxLength = 100 }: { text: string; maxLength?: number },
 *   context: any
 * ) {
 *   // Implementation
 * }
 * 
 * // Option 3: Minimal
 * @Tool()
 * async calculateSum(
 *   { numbers }: { numbers: number[] },
 *   context: any
 * ) {
 *   // Implementation
 * }
 */ 